import { apiSlice } from '../api/apiSlice';

/**
 * Read-only team attendance for managers and Jr managers. The backend resolves
 * the requester's team and returns only those reps (admins see every active
 * sales rep); `scope` says which applied.
 */

export interface TeamAttendanceScope {
  kind: 'all' | 'team';
  teamSize: number;
}

export type TeamCheckinStatus =
  | 'pending'
  | 'on_time'
  | 'offsite_click'
  | 'no_click'
  | 'late_on_site'
  | 'not_expected'
  | 'email_unavailable'
  | 'no_record';

export interface TeamCheckinDayRep {
  employeeId: string;
  name: string;
  teamName: string | null;
  status: TeamCheckinStatus;
  statusLabel: string;
  statusDetail: string;
  clickedAt: string | null;
  clickedAtLabel: string | null;
}

export interface TeamCheckinDay {
  scope: TeamAttendanceScope;
  workDate: string;
  workDateLabel: string;
  isToday: boolean;
  stage: 'before_cutoff' | 'late_before_recheck' | 'after_recheck';
  onTimeDeadlineLabel: string;
  lateRecheckLabel: string;
  summary: {
    teamSize: number;
    expected: number;
    onTime: number;
    late: number;
    offsite: number;
    noClick: number;
    emailUnavailable: number;
    pending: number;
    notExpected: number;
    noRecord: number;
  };
  reps: TeamCheckinDayRep[];
}

export interface TeamCheckinRepStats {
  employeeId: string;
  name: string;
  teamName: string | null;
  expectedDays: number;
  onTimeCount: number;
  lateCount: number;
  noClickCount: number;
  offsiteCount: number;
  pendingCount: number;
  issueCount: number;
  /** Null until the rep has at least one decided day in the window. */
  onTimeRate: number | null;
  issueRate: number | null;
  lateRate: number | null;
  averageCheckinMinutes: number | null;
  averageCheckinLabel: string | null;
}

export interface TeamCheckinAnalytics {
  scope: TeamAttendanceScope;
  startDate: string;
  endDate: string;
  days: number;
  totals: {
    expectedDays: number;
    onTimeCount: number;
    lateCount: number;
    noClickCount: number;
    offsiteCount: number;
    pendingCount: number;
    onTimeRate: number | null;
    issueRate: number | null;
    lateRate: number | null;
  };
  reps: TeamCheckinRepStats[];
}

export interface TeamCheckinHistory {
  scope: TeamAttendanceScope;
  employee: TeamCheckinRepStats;
  ranks: {
    peerCount: number;
    onTimeRateRank: number | null;
    averageCheckinRank: number | null;
    issueRateRank: number | null;
  };
  history: Array<{
    workDate: string;
    workDateLabel: string;
    status: TeamCheckinStatus;
    statusLabel: string;
    statusDetail: string;
    clickedAt: string | null;
    clickedAtLabel: string | null;
  }>;
}

export type TeamWeekendCheckpointStatus =
  | 'scheduled'
  | 'awaiting_checkin'
  | 'confirmed'
  | 'offsite_click'
  | 'window_closed';

export interface TeamWeekendCheckpoint {
  status: TeamWeekendCheckpointStatus;
  label: string;
  at: string | null;
}

export interface TeamWeekendRosterRep {
  employeeId: string;
  name: string;
  teamName: string | null;
  shiftCount: number;
  standardShiftCount: number;
  holidayShiftCount: number;
  statusLabel: string;
  submittedAt: string | null;
  missedCheckins: number;
  shifts: Array<{
    key: string;
    label: string;
    start: TeamWeekendCheckpoint;
    end: TeamWeekendCheckpoint;
  }>;
  suspension: {
    isSuspended: boolean;
    endsOnLabel: string | null;
    daysRemaining: number;
    lastViolationWeekendLabel: string | null;
    lastViolationReason: string | null;
  };
}

export interface TeamWeekendRoster {
  scope: TeamAttendanceScope;
  weekendStartDate: string;
  weekendLabel: string;
  holiday: { enabled: boolean; holidayDay: 'friday' | 'monday' | null; holidayLabel: string | null };
  shiftOptions: Array<{ key: string; shortLabel: string; fullLabel: string }>;
  shiftAvailability: Array<{
    key: string;
    count: number;
    capacity: number | null;
    remaining: number | null;
    isFull: boolean;
    uncapped: boolean;
  }>;
  summary: {
    teamSize: number;
    signedUp: number;
    withoutShifts: number;
    totalShiftSignups: number;
    missedStartCount: number;
    missedEndCount: number;
    missedRepCount: number;
    suspendedCount: number;
  };
  reps: TeamWeekendRosterRep[];
}

export interface TeamWeekendAnalytics {
  scope: TeamAttendanceScope;
  startWeekendDate: string;
  endWeekendDate: string;
  rangeLabel: string;
  totalWeekends: number;
  totals: {
    totalShiftSignups: number;
    signedUpRepCount: number;
    zeroSignupRepCount: number;
    missedStartCheckins: number;
    missedEndCheckins: number;
    offsiteStartClicks: number;
    offsiteEndClicks: number;
  };
  weekends: Array<{
    weekendStartDate: string;
    weekendLabel: string;
    teamSize: number;
    signedUp: number;
    withoutShifts: number;
    totalShiftSignups: number;
    missedStartCount: number;
    missedEndCount: number;
  }>;
  reps: Array<{
    employeeId: string;
    name: string;
    teamName: string | null;
    totalShiftSignups: number;
    weekendsSignedUp: number;
    weekendsWithoutShifts: number;
    missedStartCount: number;
    missedEndCount: number;
    offsiteStartCount: number;
    offsiteEndCount: number;
    isSuspended: boolean;
  }>;
}

type Envelope<T> = { success: boolean; data: T };

export const teamAttendanceApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getTeamCheckinDay: builder.query<TeamCheckinDay, { workDate: string }>({
      query: (params) => ({ url: '/team-attendance/checkin/day', params }),
      transformResponse: (response: Envelope<TeamCheckinDay>) => response.data,
    }),
    getTeamCheckinAnalytics: builder.query<TeamCheckinAnalytics, { endDate: string; days: number }>({
      query: (params) => ({ url: '/team-attendance/checkin/analytics', params }),
      transformResponse: (response: Envelope<TeamCheckinAnalytics>) => response.data,
    }),
    getTeamCheckinHistory: builder.query<TeamCheckinHistory, { employeeId: string; endDate: string; days: number }>({
      query: ({ employeeId, ...params }) => ({
        url: `/team-attendance/checkin/employees/${encodeURIComponent(employeeId)}/history`,
        params,
      }),
      transformResponse: (response: Envelope<TeamCheckinHistory>) => response.data,
    }),
    getTeamWeekendRoster: builder.query<TeamWeekendRoster, { weekendStartDate?: string }>({
      query: (params) => ({ url: '/team-attendance/weekend/roster', params }),
      transformResponse: (response: Envelope<TeamWeekendRoster>) => response.data,
    }),
    getTeamWeekendAnalytics: builder.query<
      TeamWeekendAnalytics,
      { startWeekendDate: string; endWeekendDate: string }
    >({
      query: (params) => ({ url: '/team-attendance/weekend/analytics', params }),
      transformResponse: (response: Envelope<TeamWeekendAnalytics>) => response.data,
    }),
  }),
});

export const {
  useGetTeamCheckinDayQuery,
  useGetTeamCheckinAnalyticsQuery,
  useGetTeamCheckinHistoryQuery,
  useGetTeamWeekendRosterQuery,
  useGetTeamWeekendAnalyticsQuery,
} = teamAttendanceApiSlice;
